import { useRef, useEffect } from 'react';
import { useThemeObserver } from '@atlaskit/tokens';
import { getATLContextDomain } from '@atlassian/atl-context';
import { checkThirdParty } from '@atlassian/jira-browser-storage-controls/src/utils/check-third-party/index.tsx';
import { getPendoPlugin } from '@atlassian/jira-choreographer-services/src/main.tsx';
import { environmentToEnvType } from '@atlassian/jira-choreographer-services/src/utils.tsx';
import { PRODUCT_DISCOVERY_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import normaliseUrl from '@atlassian/jira-platform-url-normalisation/src/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import type { Environment } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { APP_KEY_NAME_MAPPING } from './constants.tsx';
import { COMMOM_AREA, type IPendoWidgetProps } from './types.tsx';

import '@atlassian/pendo-global-css';

let pendingRequestJira = Promise.resolve();

function reportError(error: Error, id: string, message: string) {
	// As the operation like initialize/update/teardown are asynchronous, there's a chance that window.pendo
	// could be deleted while switching contexts between JPD and Jira hence the window.pendo might not be available and we would report these issues
	fireErrorAnalytics({
		meta: {
			id,
			packageName: 'jiraCommonPendoAnalytics',
		},
		attributes: {
			message,
		},
		error,
	});
}

const initializePendoWithChecking = async (
	apiKey: string,
	appName: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	pendoConfig: any,
	environment: Environment,
) => {
	const id = `pendo-agent-${apiKey}`;
	try {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (window.pendo && window.pendo.teardown && apiKey !== window.pendo.apiKey) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.pendo.teardown();

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			delete window.pendo;
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		reportError(error, 'jiraPendoAnalyticsTeardown', 'Failed to teardown pendo');
	}

	await checkThirdParty('pendo-main', async () => {
		const config = fg('post-office_enable_pendo_choreographer')
			? getPendoPlugin(environmentToEnvType(environment)).getPendoConfig(pendoConfig)
			: pendoConfig;

		if (
			!ff('pendo-jira-load-only-for-eng-users_q9w6l') ||
			pendoConfig?.visitor?.language_preference.startsWith('en')
		) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			let script: HTMLScriptElement | HTMLElement | null = document.getElementById(id);
			if (script) {
				// @ts-expect-error as id is not in the global window

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.pendo = window[id];
				try {
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					if (window?.globalPendoAppName === appName) {
						const updateInterval = setInterval(() => {
							if (
								// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
								window.pendo !== undefined &&
								// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
								typeof window.pendo.initialize === 'function' &&
								// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
								typeof window.pendo.updateOptions === 'function'
							) {
								// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
								if (apiKey !== window?.pendo?.apiKey) {
									// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
									window.pendo.initialize(config);
								} else {
									// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
									window.pendo.updateOptions(config);
								}
								clearInterval(updateInterval);
							}
						}, 1000);
					}
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					reportError(
						error,
						'jiraPendoAnalyticsInitializeUpdate',
						'Failed to initialize/update pendo',
					);
				}
			} else {
				// eslint-disable-next-line jira/js/no-document-script, jira/jira-ssr/no-unchecked-globals-usage
				script = document.createElement('script');
				script.id = id;
				if (script instanceof HTMLScriptElement) {
					script.defer = true;
				}
				script.onload = () => {
					// @ts-expect-error as id is not in the global window

					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					window[id] = window.pendo;
					try {
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						if (window?.globalPendoAppName === appName) {
							const initializeInterval = setInterval(() => {
								// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
								if (window.pendo !== undefined && typeof window.pendo.initialize === 'function') {
									// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
									window.pendo.initialize(config);
									clearInterval(initializeInterval);
								}
							}, 1000);
						}
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
					} catch (error: any) {
						reportError(error, 'jiraPendoAnalyticsInitialize', 'Failed to initialize pendo');
					}
				};
				if (script instanceof HTMLScriptElement) {
					script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
				}

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				document.head.appendChild(script);
			}
		}
	});
};

const addPendoWidget = (props: IPendoWidgetProps) => {
	const {
		atlassianAccountId,
		cloudId,
		cloudName,
		appEditions,
		apiKey,
		editionKey,
		appName,
		language,
		displayMode,
		environment,
	} = props;
	// Added Location Config
	const pendoConfig = {
		visitor: {
			id: atlassianAccountId,
			language_preference: language,
			display_mode: displayMode,
		},
		account: {
			id: `${appName}-${cloudId}`,
			workspace_name: cloudName,
			edition: editionKey === 'common' ? 'na' : appEditions?.[editionKey],
		},
		location: {
			transforms: [
				{
					attr: 'href',
					action: 'Replace',
					data: (href: string) => `${normaliseUrl(href)}`,
				},
				{
					attr: 'pathname',
					action: 'Replace',

					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					data: (pathname: string) => `${pathname}/${window?.globalPendoAppName}`,
				},
			],
		},
		disableGuidePseudoStyles: true,
		// Building block guides use an inline style tag for various pseudo styles (e.g. hover, carets, animations).
		// These can be turned off using this flag for application's with more restrictive CSP settings but will require custom effort to reach style parity.
		guides: {
			// If true, custom code blocks in building block guides will not be allowed to run. Also, classic guides will not be shown at all.
			preventCodeInjection: true,
			globalScripts: fg('pendo-gsac-changes-globalscrips') ? globalScripts : [],
		},
	};

	pendingRequestJira = pendingRequestJira.then(() =>
		initializePendoWithChecking(apiKey, appName, pendoConfig, environment),
	);
};

export const globalScripts = [
	{
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		script(_step: any, _guide: any) {
			const acceptedJavaScript = {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				'window.location.href': window.location.href,

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				'window.location.pathname': window.location.pathname,
			};

			const result = processFillPolls(acceptedJavaScript);
			function getValueFromFillPoll(fillPollText: string) {
				const fillPollValue = fillPollText.split('{fillPoll: "')[1].split('"/}')[0];
				return fillPollValue;
			}
			function processFillPolls(acceptedJavaScriptObject: {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				[x: string]: any;
				'window.location.href'?: string;
				'window.location.pathname'?: string;
			}) {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				const fillPollElements = window.pendo.dom(
					'._pendo-open-text-poll-question p[id*="pendo-text"]:contains("{fillPoll")',
				);
				if (fillPollElements.length > 0) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					fillPollElements.each((el: any) => {
						const javascriptRule = getValueFromFillPoll(el.innerText);
						const pollId = el.parentElement.parentElement.dataset.pendoPollId;

						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						const textarea = document.querySelector(`textarea[data-pendo-poll-id="${pollId}"]`);
						if (textarea) {
							if (textarea instanceof HTMLTextAreaElement) {
								// Now you can safely use textarea as an HTMLTextAreaElement
								textarea.value = `URL=${acceptedJavaScriptObject[javascriptRule]}`;
							}
						}
					});
				}

				return fillPollElements;
			}
			return result;
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		test(step: any, _guide: any) {
			return step;
		},
	},
	{
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		script(_step: any, _guide: any) {
			const result = processHiddenRows();

			function processHiddenRows() {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				const hiddenRows = window.pendo.dom('div[id*="pendo-text"]:contains("{hidden/}")');
				if (hiddenRows) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					hiddenRows.each((el: any) => {
						const elUpdated = el;
						elUpdated.style.visibility = 'hidden';
						elUpdated.style.display = 'none';
						if (elUpdated.dataset.pendoPollId) {
							// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
							const linkedPollElements = window.pendo.dom(
								`[data-pendo-poll-id="${elUpdated.dataset.pendoPollId}"]`,
							);
							if (linkedPollElements) {
								linkedPollElements.each(
									(linkedPollEl: {
										nodeName: string;
										parentNode: {
											parentNode: { style: { visibility: string; display: string } };
										};
										style: { visibility: string; display: string };
									}) => {
										const updatedLinkedPollEl = linkedPollEl;
										if (updatedLinkedPollEl.nodeName === 'FORM') {
											updatedLinkedPollEl.parentNode.parentNode.style.visibility = 'hidden';
											updatedLinkedPollEl.parentNode.parentNode.style.display = 'none';
										} else {
											updatedLinkedPollEl.style.visibility = 'hidden';
											updatedLinkedPollEl.style.display = 'none';
										}
									},
								);
							}
						}
					});
				}
				return hiddenRows;
			}
			return result;
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		test(step: any, _guide: any) {
			return step;
		},
	},
	{
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		script(_step: any, _guide: any) {
			const result = processDeepLinkButton();

			function processDeepLinkButton() {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				const button = window.pendo?.dom('button[id*="pendo-button"]');
				if (button.length > 0 && button[0].innerHTML.includes('{{billing-preview-pendo}}')) {
					button[0].innerHTML = button[0].innerHTML.replace('{{billing-preview-pendo}}', '');
					button.on('click', () => {
						const CLOUD_ADMIN_URL = `https://${getATLContextDomain('admin')}`;
						let newurl = CLOUD_ADMIN_URL; // default if newurl build fails

						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						const accountid = window.pendo?.getSerializedMetadata().account.id;
						const dashindex = accountid.indexOf('-');
						const cloudId = accountid.substring(dashindex + 1); // strip out "jsw-" from account.id to get cloudId
						if (cloudId.length > 10) {
							newurl = `${CLOUD_ADMIN_URL}/s/${cloudId}/billing-preview`;
						}

						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						window.pendo?.onGuideDismissed();

						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						window.open(newurl, '_blank');
					});
				}
				return button;
			}
			return result;
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		test(step: any, _guide: any) {
			return step;
		},
	},
];

export const PendoAnalytics = () => {
	const tenantContext = useTenantContext();
	const { colorMode } = useThemeObserver();
	const loadingRef = useRef(false);
	const { loading, error, data } = useProjectContext();
	const environment = useEnvironment();
	useEffect(() => {
		// If the project context is loading, we don't want to render the Pendo widget
		// we only want to show pendo when we are inside a project
		if (loading) {
			return;
		}

		// If the project context is loaded, let's make sure that there was no
		// errors and the data exists.
		const projectContextLoaded = !loading && !error && data;

		const isProjectLoaded = loadingRef.current || projectContextLoaded;
		loadingRef.current = loading;

		// Pendo Snippet is only for JWM JSM JSW products and NOT for JPD as they have separate implementation
		if (isProjectLoaded && data?.projectType === PRODUCT_DISCOVERY_PROJECT) {
			return;
		}

		// as checked above, there should always be projectType, but we'll provide a
		// sensible default just in case
		const jiraAppName = data?.projectType || COMMOM_AREA;
		const { apiKey, appName, editionKey } = APP_KEY_NAME_MAPPING[jiraAppName];

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.globalPendoAppName = appName;

		// by this point, we know that we are in a project and we have the correct
		// data to render the pendo widget
		const pendoProps: IPendoWidgetProps = {
			atlassianAccountId: tenantContext.atlassianAccountId,
			cloudId: tenantContext.cloudId,
			cloudName: tenantContext.cloudName,
			appEditions: tenantContext?.appEditions,
			// By default, the colorThemeObserver will return undefined - that means it is light
			displayMode: colorMode || 'light',
			language: tenantContext.locale,
			appName, // Fix: Cast appName to AppName type
			apiKey,
			editionKey, // Assign a default value of type EditionKey
			environment,
		};

		addPendoWidget(pendoProps);

		return () => {
			// In non-project area guides should not be displayed. setting it empty means location API will not display any guides.

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.globalPendoAppName = '';
		};
	}, [
		loading,
		error,
		data,
		tenantContext,
		tenantContext.atlassianAccountId,
		tenantContext.cloudId,
		tenantContext.cloudName,
		tenantContext.locale,
		colorMode,
		loadingRef,
		environment,
	]);

	return null;
};
